import { Box } from "@otrium/core";
import { useContext, useEffect, useState, JSX } from "react";
import { useDispatch } from "react-redux";
import { t } from "@lingui/macro";
import { useRouter } from "next/router";
import { capitalize } from "lodash-es";
import { PageHead } from "src/molecules/PageHead";
import { ProductsViewSkeleton } from "src/molecules/ProductsViewSkeleton";
import { StyledBrandView } from "src/modules/brands/BrandView.styled";
import { PLPType } from "src/types/PLPType.d";
import { PopularBrandsWrapper } from "src/organisms/PopularBrands";
import { RRPFootnote } from "src/molecules/RRPFootnote";
import { AlgoliaProductList } from "src/organisms/AlgoliaProductList";
import { AppCtx } from "src/contexts/app.context";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import getAllBrandsPageRoute from "src/utils/getAllBrandsPageRoute";
import getDeepLink from "src/utils/getDeepLink";
import { setGender } from "src/modules/page";
import { ShopType } from "src/types/graphql.d";
import { useLingui } from "@lingui/react";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";

const AllProductsView = (): JSX.Element => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { i18n } = useLingui();
  const { locale } = useContext(AppCtx);
  const [algoliaLoading, setAlgoliaLoading] = useState(false);
  const { slug, cat_slug, discount } = router.query as {
    slug: string;
    discount?: string;
    cat_slug?: string | string[];
  };
  const localeGenderId = localeFix({ gender: slug });
  const capitalisedGenderId = capitalize(slug);
  const viewBrandsLink = getAllBrandsPageRoute({
    shopType: localeGenderId as ShopType,
    locale,
  });
  const { enablePLPIndexation } = useFeatureFlags();

  useEffect(() => {
    dispatch(setGender(localeGenderId));
  }, [dispatch, localeGenderId]);

  const description = t(
    i18n
  )`Explore all items for ${slug} at Otrium. Shop top brands with discounts of up to 70%. Take advantage and order today!`;

  return (
    <StyledBrandView>
      {algoliaLoading && (
        <Box
          mt={3}
          sx={{ position: "absolute", zIndex: 15 }}
          width="100%"
          bg="White"
        >
          <ProductsViewSkeleton />
        </Box>
      )}
      <PageHead
        title={t(i18n)`${
          capitalisedGenderId ? `${capitalisedGenderId} | ` : ""
        }Online Outlet | Otrium`}
        deeplink={getDeepLink({
          hostname: `${localeGenderId}/products`,
          query: { discount, cat_slug },
        })}
        description={description}
        isPDP={!!enablePLPIndexation}
      />
      <AlgoliaProductList
        hideBreadcrumbs
        hidePromotions
        genderId={slug}
        locale={locale}
        plpType={PLPType.SHOP_TYPE}
        excludedFacetsList={[]}
        setLoading={setAlgoliaLoading}
        slug="products"
        title={<Box mt="space32">{capitalisedGenderId}</Box>}
      />
      <PopularBrandsWrapper
        localeGenderId={localeGenderId}
        genderId={slug}
        viewAllLink={viewBrandsLink}
        plpType={PLPType.SHOP_TYPE}
      />
      <RRPFootnote />
    </StyledBrandView>
  );
};

export default AllProductsView;
